import type { AlertColor } from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { refuseSignature } from "../../../api/signer";

interface RefuseSignatureModalProps {
  id: string;
  linkId?: string;
  linkCode?: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  setMessageAlert: (string: string) => void;
  setTypeAlert: (string: AlertColor) => void;
  setOpenAlert: (boolean: boolean) => void;
}

export default function RefuseSignatureModal({
  open,
  onClose,
  onSuccess,
  setMessageAlert,
  setOpenAlert,
  setTypeAlert,
  id,
  linkId,
  linkCode,
}: RefuseSignatureModalProps): React.JSX.Element {
  const { t } = useTranslation();
  const [motivo, setMotivo] = useState("");
  const [errorField, setErrorField] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = (): void => {
    if (isSubmitting) return;
    setMotivo("");
    setErrorField("");
    onClose();
  };

  const handleConfirm = async (): Promise<void> => {
    const trimmed = motivo.trim();
    if (!trimmed) {
      setErrorField(t("sign.refuseModal.errorRequired", "O motivo da recusa é obrigatório."));
      return;
    }
    if (trimmed.length > 1000) {
      setErrorField(t("sign.refuseModal.errorMaxLength", "O motivo deve ter no máximo 1000 caracteres."));
      return;
    }

    try {
      setIsSubmitting(true);
      const code = linkCode || linkId || "";
      await refuseSignature(id, {
        linkCode: code,
        reason: trimmed,
      });

      setTypeAlert("success");
      setMessageAlert(t("sign.refuseModal.successMessage", "Assinatura recusada com sucesso."));
      setOpenAlert(true);
      setMotivo("");
      setErrorField("");
      onClose();
      onSuccess();
    } catch (e) {
      setTypeAlert("error");
      setMessageAlert(t("sign.refuseModal.errorMessage", "Não foi possível recusar a assinatura. Tente novamente."));
      setOpenAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" onClose={handleClose} open={open}>
      <DialogTitle>
        <Typography fontWeight={600} variant="h6">
          {t("sign.refuseModal.title", "Recusar Assinatura")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
          {t(
            "sign.refuseModal.description",
            "Ao recusar a assinatura, você não poderá mais assinar este documento. Por favor, informe o motivo da recusa abaixo."
          )}
        </Typography>

        <TextField
          autoFocus
          error={Boolean(errorField)}
          fullWidth
          helperText={errorField || `${motivo.length}/1000`}
          label={t("sign.refuseModal.reasonLabel", "Motivo da recusa")}
          multiline
          onChange={(e) => {
            setMotivo(e.target.value);
            if (errorField) setErrorField("");
          }}
          placeholder={t("sign.refuseModal.placeholder", "Explique o motivo pelo qual está recusando a assinatura deste documento...")}
          rows={4}
          slotProps={{
            htmlInput: {
              maxLength: 1000,
            },
          }}
          value={motivo}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button disabled={isSubmitting} onClick={handleClose} variant="outlined">
          {t("sign.refuseModal.cancel", "Cancelar")}
        </Button>
        <Button
          color="error"
          disabled={isSubmitting || !motivo.trim()}
          onClick={handleConfirm}
          variant="contained"
        >
          {isSubmitting
            ? t("sign.refuseModal.submitting", "Recusando...")
            : t("sign.refuseModal.confirm", "Confirmar Recusa")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
